import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClock,
  faPen,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { subscribeToPanelData } from '../../services/PanelDataService';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';

export const FORM_TABS = [
  { key: 'registration', label: 'Registration', id: '68c7eefbd44999eedb541043' },
  { key: 'accreditation', label: 'Accreditation', id: '68e6505866f25479291b933f' },
  { key: 'travel', label: 'Travel', id: '68e781408e1384b2c283c3af' },
  { key: 'accommodation', label: 'Accommodation', id: '68e784018e1384b2c283c3bb' },
  { key: 'guest', label: 'Guest', id: '68e785cb8e1384b2c283c3c5' },
];

/** Next form tab after a successful submit (skips athlete-hidden travel/accommodation). */
export const getNextFormTab = (currentKey, isAthlete = false, hideGuestTab = false) => {
  const visibleTabs = FORM_TABS.filter((tab) => {
    if (hideGuestTab && tab.key === 'guest') return false;
    if (isAthlete && ['travel', 'accommodation'].includes(tab.key)) return false;
    return true;
  });
  const currentIndex = visibleTabs.findIndex((tab) => tab.key === currentKey);
  if (currentIndex < 0 || currentIndex >= visibleTabs.length - 1) return null;
  return visibleTabs[currentIndex + 1];
};

const firstText = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

const objectLabel = (value, keys) => {
  if (!value || typeof value !== 'object') return '';
  return firstText(...keys.map((key) => value[key]));
};

/**
 * Portal header avatar = CRM profile / editProfilePhoto only.
 * Never use person_data.photo (accreditation badge → S3 Accreditation/2028).
 */
const resolveHeaderProfileImage = (personData, person, apiUrl) => {
  const edit = personData?.editProfilePhoto;
  const editEntry = Array.isArray(edit) ? edit[0] : edit;
  const editUrl =
    typeof editEntry === 'string'
      ? editEntry.trim()
      : String(editEntry?.url || '').trim();
  if (editUrl) return resolvePersonImageUrl(editUrl, apiUrl) || editUrl;

  const crmImage =
    person?.image ||
    personData?.profileImage ||
    '';
  return resolvePersonImageUrl(crmImage, apiUrl) || '';
};

/** Option 1c: integer percent from { completed, total }, or null when unknown. */
export const tabPercentFromProgress = (progress) => {
  const total = Number(progress?.total);
  const completed = Number(progress?.completed);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (!Number.isFinite(completed) || completed < 0) return null;
  return Math.round((completed / total) * 100);
};

const emptyTabProgress = () => ({
  registration: null,
  accreditation: null,
  travel: null,
  accommodation: null,
  guest: null,
});

const UserFormPrototypeHeader = ({
  activeSegment,
  canSwitchTabs,
  isAthlete,
  hideGuestTab = false,
  onTabSelect,
  participant,
  person,
  tabProgress: tabProgressProp,
  tabs: tabsProp,
}) => {
  const [subscribedProgress, setSubscribedProgress] = useState(emptyTabProgress);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const tabs = Array.isArray(tabsProp) && tabsProp.length ? tabsProp : FORM_TABS;
  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (tabProgressProp) return undefined;
    return subscribeToPanelData((data) => {
      setSubscribedProgress({
        registration: data.registration || null,
        accreditation: data.accreditation || null,
        travel: data.travel || null,
        accommodation: data.accommodation || null,
        guest: data.guest || null,
      });
    });
  }, [tabProgressProp]);

  const tabProgress = tabProgressProp || subscribedProgress;
  const data = participant?.person_data || {};
  const firstName = firstText(
    person?.name,
    participant?.person_id?.name,
    data.givenFirstName
  );
  const lastName = firstText(
    person?.lname,
    participant?.person_id?.lname,
    data.familyLastName
  );
  const displayName = `${firstName} ${lastName}`.trim() || 'Participant';
  const registrationStatus = participant?.registration_status || 'Added';
  const approvalStatus = participant?.approval_status || 'Pending';
  const approvalLabel =
    approvalStatus === 'Pending' ? 'Pending Approval' : approvalStatus;
  const group = objectLabel(participant?.group_id, ['group_name', 'name']) || data.groupName;
  const role = objectLabel(participant?.role_id, ['role_name', 'name']);
  const invitation = objectLabel(participant?.invitation_type_id, [
    'invitation_type',
    'invitation_type_name',
    'name',
  ]);
  const accreditation = objectLabel(participant?.accreditation_category_id, [
    'category_name',
    'accreditation_category',
    'name',
  ]);
  const crmPerson = person || participant?.person_id || null;
  const image = resolveHeaderProfileImage(data, crmPerson, apiUrl);

  useEffect(() => {
    setAvatarFailed(false);
  }, [image]);

  return (
    <div className="user-form-context-row">
      <section className="user-form-profile" aria-label="Participant summary">
        <div className="user-form-profile__identity">
          <div className="user-form-profile__avatar">
            {image && !avatarFailed ? (
              <img
                src={image}
                alt=""
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <FontAwesomeIcon icon={faUser} />
            )}
            <span className="user-form-profile__edit" aria-hidden="true">
              <FontAwesomeIcon icon={faPen} />
            </span>
          </div>
          <div>
            <h1>{displayName}</h1>
          </div>
        </div>

        <div className="user-form-profile__metadata">
          <span className="user-form-badge user-form-badge--registered">
            <FontAwesomeIcon icon={faCheckCircle} />
            {registrationStatus}
          </span>
          <span className="user-form-badge user-form-badge--pending">
            <FontAwesomeIcon icon={faClock} />
            {approvalLabel}
          </span>
          <span className="user-form-profile__divider" aria-hidden="true" />
          {group && <span className="user-form-chip">Group: {group}</span>}
          {role && <span className="user-form-chip">Role: {role}</span>}
          {invitation && <span className="user-form-chip">Invitation: {invitation}</span>}
          {accreditation && (
            <span className="user-form-chip">Accreditation: {accreditation}</span>
          )}
        </div>
      </section>

      <nav className="user-form-tabs" aria-label="Participant forms">
        <div className="user-form-tabs__track">
          {tabs.filter((tab) => {
            if (hideGuestTab && tab.key === 'guest') return false;
            if (isAthlete && ['travel', 'accommodation'].includes(tab.key)) return false;
            return true;
          }).map((tab) => {
            const isActive = activeSegment === tab.key;
            const isLocked = !isActive && !canSwitchTabs;
            // Always show a % badge (0 when unknown/empty) so every form has visible status.
            const realPct = tabPercentFromProgress(tabProgress?.[tab.key]);
            const displayPct = realPct == null ? 0 : realPct;
            const isComplete = realPct === 100;

            return (
              <button
                key={tab.key}
                type="button"
                className={[
                  'user-form-tab',
                  isActive ? 'user-form-tab--active' : '',
                  isLocked ? 'user-form-tab--locked' : '',
                  isComplete ? 'user-form-tab--complete' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={isLocked}
                title={
                  isLocked
                    ? 'Complete and save the current form first'
                    : tab.label
                }
                onClick={() => onTabSelect?.(tab)}
              >
                <span className="user-form-tab__label">{tab.label}</span>
                {displayPct != null && (
                  <span
                    className="user-form-tab__pct"
                    aria-label={`${displayPct} percent complete`}
                  >
                    {displayPct}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default UserFormPrototypeHeader;
